import { load } from 'cheerio';
import Provider from '@/enums/Provider';
import IImage from '@/interfaces/IImage';
import { removeCloudinaryTransform } from '@/utils/removeCloudinaryTransform';
import UrlTypes from '@/enums/UrlTypes';

export function extractProductImages(
    htmlSource: string,
    pathname: string,
    provider: Provider
): IImage[] {
    const $ = load(htmlSource);
    const productImages: IImage[] = [];

    switch (provider) {
        case Provider.KBS: {
            // Try new selector first (anchor tags with Cloudinary URLs)
            $('a[href*="res.cloudinary.com/kbs"] img').each((i, img) => {
                const $img = $(img);
                const $link = $img.parent('a');

                // Prefer the href (larger image) over src (thumbnail) when available
                const href = $link.attr('href');
                const src = $img.attr('src');
                const alt = $img.attr('alt') || $link.attr('title') || '';

                // Use href if available (full-size image), otherwise use src
                const imageUrl = href || src;

                if (imageUrl) {
                    // Ensure URL starts with https
                    const fullUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
                    const cleanSrc = removeCloudinaryTransform(fullUrl);

                    // Only add image if we have a valid URL (either cleaned or original)
                    const finalSrc = cleanSrc || fullUrl;

                    productImages.push({
                        parentPathname: pathname,
                        src: finalSrc,
                        alt: alt,
                        type: UrlTypes.PRODUCT,
                    });
                }
            });

            // Fallback to old selector if no images found (for backward compatibility)
            if (productImages.length === 0) {
                $('figure.image-box img').each((i, img) => {
                    const src = $(img).attr('src');
                    const alt = $(img).attr('alt') || '';

                    if (src) {
                        const cleanSrc = removeCloudinaryTransform(src);
                        // Use cleaned URL if available, otherwise use original
                        const finalSrc = cleanSrc || src;

                        productImages.push({
                            parentPathname: pathname,
                            src: finalSrc,
                            alt: alt,
                            type: UrlTypes.PRODUCT,
                        });
                    }
                });
            }
            break;
        }
        case Provider.IO:
            // Add logic for Provider.IO
            // Select only images within the div.io_image_thumbbox
            $('div.io_image_thumbbox img').each((i, img) => {
                const src = $(img).attr('src');
                const alt = $(img).attr('alt') || $(img).attr('title') || '';
                if (src) {
                    const bigSrc = transformToBigImage(src);
                    productImages.push({
                        parentPathname: pathname,
                        src: bigSrc,
                        alt: alt,
                        type: UrlTypes.PRODUCT,
                    });
                }
            });
            break;
        case Provider.ERS:
            // can only find one image the rest are not in the source
            $('[id^="container_"] img, .image-wrapper img').each((i, img) => {
                const src = $(img).attr('src');
                const alt = $(img).attr('alt') || $(img).attr('title') || '';
                if (src) {
                    productImages.push({
                        parentPathname: pathname,
                        src: src,
                        alt,
                        type: UrlTypes.PRODUCT
                    });
                }
            });
            break;
        default:
            break;
    }
    return Array.from(productImages);
}

/**
 * Transform a thumbnail URL to its corresponding _big version.
 * If the URL already includes _big right before the file extension, it is returned unchanged.
 * Otherwise, it inserts '_big' before the file extension.
 */
function transformToBigImage(url: string): string {
    // Check if the URL already contains _big right before the extension.
    if (/_big(\.[^.]+)$/.test(url)) {
        return url;
    }
    // Otherwise, insert _big before the file extension.
    return url.replace(/(\.[^.]+)$/, '_big$1');
}